import type { ParticipantStatus } from '@localagent/sdk';
import { cn } from '@localagent/ui/lib/utils';
import { MAIN_TAB_LABEL, QUESTIONS_TAB_ID, type ChatTab } from './chat-tabs';

export interface ChatTabsProps {
  tabs: ChatTab[];
  selectedId: string | null;
  onSelect: (participantId: string | null) => void;
  onClose: (participantId: string) => void;
  testIdPrefix?: string;
  questionsTab?: { count: number };
}

const STATUS_LABEL: Record<ParticipantStatus, string> = {
  running: 'Running',
  idle: 'Idle',
  settled: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

const STATUS_CLASS: Record<ParticipantStatus, string> = {
  running: 'text-info-text',
  idle: 'text-muted-foreground',
  settled: 'text-success-text',
  failed: 'text-error-text',
  stopped: 'text-muted-foreground',
};

export function ChatTabs({ tabs, selectedId, onSelect, onClose, testIdPrefix = '', questionsTab }: ChatTabsProps) {
  const questionsSelected = questionsTab !== undefined && selectedId === QUESTIONS_TAB_ID;
  const selected = questionsSelected ? null : (tabs.find((t) => t.participantId === selectedId) ?? null);
  return (
    <div className="border-b border-border" data-testid={`${testIdPrefix}chat-tabs`}>
      <div role="tablist" aria-label="Chat participants" className="flex items-stretch gap-1 overflow-x-auto px-3 pt-1.5">
        <button
          type="button"
          role="tab"
          aria-selected={selected === null && !questionsSelected}
          data-testid={`${testIdPrefix}chat-tab-main`}
          onClick={() => onSelect(null)}
          className={cn(
            'rounded-t-md px-3 py-1.5 text-caption font-medium transition-colors',
            selected === null && !questionsSelected ? 'bg-selected text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {MAIN_TAB_LABEL}
        </button>
        {questionsTab ? (
          <div
            data-testid={`${testIdPrefix}chat-tab-questions`}
            className={cn(
              'flex items-center gap-1.5 rounded-t-md pl-3 pr-1 transition-colors',
              questionsSelected ? 'bg-selected text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <button
              type="button"
              role="tab"
              aria-selected={questionsSelected}
              data-testid={`${testIdPrefix}chat-tab-questions-select`}
              onClick={() => onSelect(QUESTIONS_TAB_ID)}
              className="flex items-center gap-1.5 py-1.5 text-caption font-medium"
            >
              <span>Questions</span>
              {questionsTab.count > 0 ? (
                <span data-testid={`${testIdPrefix}chat-tab-questions-count`} className="text-micro text-info-text">
                  {questionsTab.count}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              aria-label="Close Questions"
              data-testid={`${testIdPrefix}chat-tab-questions-close`}
              onClick={() => onClose(QUESTIONS_TAB_ID)}
              className="rounded-sm px-1 text-caption text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              ×
            </button>
          </div>
        ) : null}
        {tabs.map((tab) => {
          const isSelected = tab.participantId === selected?.participantId;
          return (
            <div
              key={tab.participantId}
              data-testid={`${testIdPrefix}chat-tab`}
              data-participant-id={tab.participantId}
              data-status={tab.status}
              data-background={tab.background ? 'true' : 'false'}
              className={cn(
                'flex items-center gap-1.5 rounded-t-md pl-3 pr-1 transition-colors',
                isSelected ? 'bg-selected text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <button
                type="button"
                role="tab"
                aria-selected={isSelected}
                data-testid={`${testIdPrefix}chat-tab-select`}
                onClick={() => onSelect(tab.participantId)}
                className="flex items-center gap-1.5 py-1.5 text-caption font-medium"
              >
                <span className="max-w-48 truncate">{tab.label}</span>
                <span className={cn('text-micro', STATUS_CLASS[tab.status])}>{STATUS_LABEL[tab.status]}</span>
                {tab.background ? <span className="text-micro text-muted-foreground">Background</span> : null}
              </button>
              <button
                type="button"
                aria-label={`Close ${tab.label}`}
                data-testid={`${testIdPrefix}chat-tab-close`}
                onClick={() => onClose(tab.participantId)}
                className="rounded-sm px-1 text-caption text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      {selected ? (
        <nav
          aria-label="Owner chain"
          data-testid={`${testIdPrefix}chat-tab-owner-chain`}
          className="flex flex-wrap items-center gap-1 px-4 py-1 text-micro text-muted-foreground"
        >
          {selected.ownerChain.map((label, i) => (
            <span key={`${i}-${label}`} className="flex items-center gap-1">
              {i > 0 ? <span aria-hidden>›</span> : null}
              <span className={i === selected.ownerChain.length - 1 ? 'text-foreground' : undefined}>{label}</span>
            </span>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
